import {
	ACCEPTED_FILE_CONTENT_TYPES,
	FILE_HTTP_UPLOAD_DOMAINS,
	FILE_MAX_PART_COUNT,
	FILE_MAX_SIZE_BYTES,
	FileDomain,
	ValidationErrorCode,
} from '@today/types'
import { z } from 'zod'

// ─── Field-level building blocks ─────────────────────────────────────────────
//
// Each boundary carries its machine-readable code as the issue's `message`, so
// the controller maps a rejection to the contract's 422 code without having to
// re-derive which field failed. `fileValidationCode` reads it back.

/** Longest `fileName` the contract accepts, in Unicode code points. */
const FILE_NAME_MAX_CODE_POINTS = 255

/** Longest `entityType` / `entityId` the contract accepts. */
const ENTITY_REF_MAX_LENGTH = 64

/**
 * `fileName` — 1–255 Unicode CODE POINTS after trimming.
 *
 * Counted with the spread, not `.length`: a string's `.length` is UTF-16 code
 * units, so an emoji or any astral-plane character would count twice and a name
 * the contract accepts would be refused.
 */
const fileNameSchema = z
	.string()
	.trim()
	.refine((value) => {
		const codePoints = [...value].length
		return codePoints >= 1 && codePoints <= FILE_NAME_MAX_CODE_POINTS
	}, ValidationErrorCode.FILE_NAME_INVALID)

/** `contentType` — a member of the accepted media-type allowlist. */
const contentTypeSchema = z.enum(ACCEPTED_FILE_CONTENT_TYPES, {
	message: ValidationErrorCode.FILE_CONTENT_TYPE_INVALID,
})

/** `sizeBytes` — an integer in 1–5368709120 (5 GiB). */
const sizeBytesSchema = z
	.number({ message: ValidationErrorCode.FILE_SIZE_INVALID })
	.int(ValidationErrorCode.FILE_SIZE_INVALID)
	.min(1, ValidationErrorCode.FILE_SIZE_INVALID)
	.max(FILE_MAX_SIZE_BYTES, ValidationErrorCode.FILE_SIZE_INVALID)

/**
 * `domain` — `USER_UPLOAD` (the default when absent) or `PRODUCT_IMAGE`. The
 * other domains are written only by their own paths, so naming one here is
 * refused (TO-443 BR-2).
 */
const uploadDomainSchema = z
	.enum(FILE_HTTP_UPLOAD_DOMAINS, { message: ValidationErrorCode.FILE_DOMAIN_INVALID })
	.default(FileDomain.USER_UPLOAD)

/** `partCount` — an integer in 1–10000 (the S3 ceiling). */
const partCountSchema = z
	.number({ message: ValidationErrorCode.FILE_MULTIPART_PART_COUNT_INVALID })
	.int(ValidationErrorCode.FILE_MULTIPART_PART_COUNT_INVALID)
	.min(1, ValidationErrorCode.FILE_MULTIPART_PART_COUNT_INVALID)
	.max(FILE_MAX_PART_COUNT, ValidationErrorCode.FILE_MULTIPART_PART_COUNT_INVALID)

/**
 * One half of the opaque entity reference. Never interpreted — file-service
 * does not know what an `"offer"` is and never checks it against another table.
 */
const entityRefPartSchema = z
	.string()
	.min(1, ValidationErrorCode.FILE_ENTITY_REF_INVALID)
	.max(ENTITY_REF_MAX_LENGTH, ValidationErrorCode.FILE_ENTITY_REF_INVALID)
	.nullish()

/**
 * `entityType` and `entityId` are both null or both present — never one alone.
 * Applied as an object-level refinement because neither field can see the other.
 */
const entityRefPairing = <T extends { entityType?: string | null; entityId?: string | null }>(value: T): boolean => {
	const hasType = value.entityType !== null && value.entityType !== undefined
	const hasId = value.entityId !== null && value.entityId !== undefined
	return hasType === hasId
}

const ENTITY_REF_PAIRING_ISSUE = {
	message: ValidationErrorCode.FILE_ENTITY_REF_INVALID,
	path: ['entityType'],
}

// ─── Request schemas ─────────────────────────────────────────────────────────

/**
 * Body of `POST /files/upload-url`. The owning tenant and owner are resolved
 * from the authenticated caller, never the body, so neither appears here. The
 * storage key is derived by the service — `fileName` is metadata only and never
 * reaches a key.
 */
export const fileUploadUrlRequestSchema = z
	.object({
		fileName: fileNameSchema,
		contentType: contentTypeSchema,
		sizeBytes: sizeBytesSchema,
		entityType: entityRefPartSchema,
		entityId: entityRefPartSchema,
		domain: uploadDomainSchema,
	})
	.refine(entityRefPairing, ENTITY_REF_PAIRING_ISSUE)

/**
 * Body of `POST /files/multipart`. The same fields as a single-request upload
 * plus how many parts the caller intends to send.
 */
export const fileMultipartCreateSchema = z
	.object({
		fileName: fileNameSchema,
		contentType: contentTypeSchema,
		sizeBytes: sizeBytesSchema,
		partCount: partCountSchema,
		entityType: entityRefPartSchema,
		entityId: entityRefPartSchema,
		domain: uploadDomainSchema,
	})
	.refine(entityRefPairing, ENTITY_REF_PAIRING_ISSUE)

/** Body of `PATCH /files/{fileId}` — renaming is the only metadata mutation. */
export const fileUpdateSchema = z.object({
	fileName: fileNameSchema,
})

/**
 * Body of `POST /files/multipart/{sessionId}/complete` — the parts S3
 * acknowledged. This validates the SHAPE only; whether the presented parts
 * actually match what S3 holds is a 409 `FILE_MULTIPART_PARTS_MISMATCH` the
 * service raises after reading `ListParts`, not something a schema can know.
 */
export const fileMultipartCompleteSchema = z.object({
	parts: z
		.array(
			z.object({
				partNumber: partCountSchema,
				etag: z.string().min(1),
			}),
		)
		.min(1),
})

/** `GET /files` pagination. `pageSize` is 1–100 and defaults to 20. */
export const fileListQuerySchema = z.object({
	page: z.coerce.number().int().min(1).default(1),
	pageSize: z.coerce.number().int().min(1).max(100).default(20),
})

// ─── Code extraction ─────────────────────────────────────────────────────────

/**
 * The contract's 422 code for a rejection produced by one of the schemas above,
 * read back from the failing issue's message.
 *
 * Falls back to the generic `VALIDATION_ERROR` for an issue these schemas did
 * not author — a malformed JSON body, say — rather than mislabelling it as a
 * field boundary that did not actually fail.
 */
export const fileValidationCode = (error: z.ZodError): ValidationErrorCode => {
	const known = new Set<string>(Object.values(ValidationErrorCode))
	for (const issue of error.issues) {
		if (known.has(issue.message)) return issue.message as ValidationErrorCode
	}
	return ValidationErrorCode.VALIDATION_ERROR
}

export type FileUploadUrlRequestInput = z.infer<typeof fileUploadUrlRequestSchema>
export type FileMultipartCreateInput = z.infer<typeof fileMultipartCreateSchema>
export type FileUpdateInput = z.infer<typeof fileUpdateSchema>
export type FileMultipartCompleteInput = z.infer<typeof fileMultipartCompleteSchema>
export type FileListQueryInput = z.infer<typeof fileListQuerySchema>
